import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Alert,
  useTheme,
  Divider,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { articleService, Article, getArticleSlug } from '../services/articleService';

const ARTICLES_PER_PAGE = 9;

const Blog = () => {
  const theme = useTheme();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const data = await articleService.getArticles();
      setArticles(data);
      setError('');
    } catch (err) {
      setError('Failed to fetch articles');
      console.error('Error fetching articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'immigration-updates', label: 'Immigration Updates' },
    { value: 'student-visa', label: 'Student Visa' },
    { value: 'work-visa', label: 'Work Visa' },
    { value: 'family-visa', label: 'Family Visa' },
  ];

  const getCategoryColor = (category: string) => {
    const colors = {
      'immigration-updates': theme.palette.info.main,
      'student-visa': theme.palette.success.main,
      'work-visa': theme.palette.warning.main,
      'family-visa': theme.palette.error.main,
    };
    return colors[category as keyof typeof colors] || theme.palette.primary.main;
  };

  const filteredArticles = articles
    .filter((article) => article.status === 'published')
    .filter((article) =>
      searchTerm
        ? article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          article.description.toLowerCase().includes(searchTerm.toLowerCase())
        : true
    )
    .filter((article) =>
      selectedCategory !== 'all' ? article.category === selectedCategory : true
    )
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSkeleton = () => (
    <Grid container spacing={4}>
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item}>
          <Card>
            <Skeleton variant="rectangular" height={250} />
            <CardContent>
              <Skeleton variant="text" height={32} width="80%" />
              <Skeleton variant="text" height={24} width="40%" />
              <Skeleton variant="text" height={80} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
            }}
          >
            Latest Immigration News & Updates
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}
          >
            Stay informed about the latest changes in UK immigration laws, visa requirements,
            and important updates that might affect your immigration journey.
          </Typography>
        </Box>

        {/* Filters */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Articles Grid */}
        {loading ? (
          renderSkeleton()
        ) : (
          <>
            <Grid container spacing={4}>
              {paginatedArticles.map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article._id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    {article.featuredImage ? (
                      <CardMedia
                        component="img"
                        height="250"
                        image={article.featuredImage.url}
                        alt={article.title}
                        sx={{
                          objectFit: 'cover',
                          objectPosition: article.featuredImage.url?.includes('placehold.co')
                            ? 'center'
                            : 'top center',
                          height: '250px !important',
                          minHeight: '250px',
                          maxHeight: '250px',
                          width: '100%'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 250,
                          bgcolor: 'background.default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No image available
                        </Typography>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={article.category.split('-').join(' ').toUpperCase()}
                          size="small"
                          sx={{
                            bgcolor: `${getCategoryColor(article.category)}15`,
                            color: getCategoryColor(article.category),
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        component="h2"
                        gutterBottom
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.3,
                          fontWeight: 600,
                        }}
                      >
                        {article.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <CalendarIcon sx={{ fontSize: 16 }} />
                        {new Date(article.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {article.description}
                      </Typography>
                    </CardContent>
                    <Divider />
                    <CardActions>
                      <Button
                        component={RouterLink}
                        to={`/blog/${getArticleSlug(article)}`}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          ml: 'auto',
                          '&:hover .MuiSvgIcon-root': {
                            transform: 'translateX(4px)',
                          },
                          '& .MuiSvgIcon-root': {
                            transition: 'transform 0.2s ease',
                          },
                        }}
                      >
                        Read More
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {filteredArticles.length > ARTICLES_PER_PAGE && (
              <Stack spacing={1} alignItems="center" sx={{ mt: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {(currentPage - 1) * ARTICLES_PER_PAGE + 1}–
                  {Math.min(currentPage * ARTICLES_PER_PAGE, filteredArticles.length)} of{' '}
                  {filteredArticles.length} articles
                </Typography>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Stack>
            )}
          </>
        )}

        {/* No Results Message */}
        {!loading && filteredArticles.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No articles found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Blog; 